import {Directive, inject, Input} from '@angular/core';
import {ButtonAppConfig, ButtonFocusRingPosition, HtmlButtonType, RouteNameCapableButtonComponent} from '@aviary/buttons/button.types';
import {BUTTON_APP_CONFIG} from '@aviary/injection-tokens/app-provided.tokens';
import {Params} from '@angular/router';

@Directive({
  host: {
    '[class.mx-focus-ring-inside]': `focusRingPosition === 'inside'`,
    '[class.mx-focus-ring-outside]': `focusRingPosition === 'outside'`,
    '[class.mx-disabled]': '!enabled'
  }
})
export abstract class BaseButtonComponent implements RouteNameCapableButtonComponent {
  protected readonly config: ButtonAppConfig = inject(BUTTON_APP_CONFIG);

  @Input() public type: HtmlButtonType = 'button';
  @Input() public enabled: boolean = true;
  @Input() public tooltip: string = '';
  @Input() public disabledTooltip: string = '';

  /** External URL. Links open in a new tab with noopener/noreferrer. */
  @Input() public href?: string;

  /** These are ignored unless an app-specific [routeName] directive is applied. */
  @Input() public routeParams: Params = {};
  @Input() public queryParams?: Params;

  /** This is set when an app-specific [routeName] directive is applied. Do not set manually. */
  public genericRouteName?: string;

  public readonly focusRingPosition: ButtonFocusRingPosition = this.config.focusRingPosition;

  // Open external links in a new browser tab.
  public readonly externalLinkTarget: string = '_blank';

  // Prevent the opened page from accessing this window, and omit the referring URL.
  public readonly externalLinkRelationship: string = 'noopener noreferrer';

  public get computedTooltip(): string {
    return this.enabled || !this.disabledTooltip ? this.tooltip : this.disabledTooltip;
  }

  public get computedHref(): string | null {
    return this.enabled ? (this.href ?? null) : null;
  }

  public get computedAriaDisabled(): true | null {
    return !this.enabled || null;
  }

  public get computedTabIndex(): -1 | null {
    return this.enabled ? null : -1;
  }

  public onClick(event: MouseEvent): void {
    if (!this.enabled) event.stopPropagation();
  }
}
